import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Activity,
    AlertCircle,
    DollarSign,
    Eye,
    Globe,
    Languages,
    MapPin,
    TrendingUp,
    Users,
} from 'lucide-react';

import { getDomains } from '@/services/api';
import { getPageviewReport } from '@/services/api/pageview-report';
import {
    CompactDateRangePicker,
    CompactDomainSelect,
    fmt,
} from '@/pages/Dashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatCard } from './components/StatCard';
import { DomainsTab } from './components/DomainsTab';
import { UrlsTab } from './components/UrlsTab';
import { CountriesTab } from './components/CountriesTab';
import { PathsTab } from './components/PathsTab';

const CACHE_KEYS = {
    START_DATE: 'pageview_start_date',
    END_DATE: 'pageview_end_date',
    DOMAINS: 'pageview_domains',
    TAB: 'pageview_active_tab',
};

const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
};

type TabValue = 'domains' | 'urls' | 'countries' | 'paths';

export default function Pageview() {
    const [startDate, setStartDate] = useState(
        () => localStorage.getItem(CACHE_KEYS.START_DATE) || getYesterday(),
    );
    const [endDate, setEndDate] = useState(
        () => localStorage.getItem(CACHE_KEYS.END_DATE) || getYesterday(),
    );
    const [selectedDomains, setSelectedDomains] = useState<string[]>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.DOMAINS);
        return cached ? JSON.parse(cached) : [];
    });
    const [activeTab, setActiveTab] = useState<TabValue>(() => {
        const cached = localStorage.getItem(CACHE_KEYS.TAB) as TabValue | null;
        return cached && ['domains', 'urls', 'countries', 'paths'].includes(cached)
            ? cached
            : 'domains';
    });

    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.START_DATE, startDate);
    }, [startDate]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.END_DATE, endDate);
    }, [endDate]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.DOMAINS, JSON.stringify(selectedDomains));
    }, [selectedDomains]);
    useEffect(() => {
        localStorage.setItem(CACHE_KEYS.TAB, activeTab);
    }, [activeTab]);

    const { data: domains = [] } = useQuery({
        queryKey: ['domains'],
        queryFn: () => getDomains(),
    });

    const {
        data: report,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['pageview-report', selectedDomains, startDate, endDate],
        queryFn: () =>
            getPageviewReport({
                domains: selectedDomains,
                start: startDate,
                end: endDate,
                top_limit: 100,
            }),
        retry: 1,
    });

    const summary = report?.summary;
    const byDomain = report?.by_domain ?? [];
    const byCountry = report?.by_country ?? [];
    const byPath = report?.by_path ?? [];
    const topUrls = report?.top_urls ?? [];

    return (
        <div className="space-y-5">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Pageview Analytics
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    Análise consolidada de pageviews, sessões e métricas de monetização (RPS, RPM, IPVU)
                </p>
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <CompactDomainSelect
                    value={selectedDomains}
                    onChange={setSelectedDomains}
                    domains={domains}
                />
                <div className="hidden sm:block h-5 w-px bg-gray-200" />
                <CompactDateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={(s, e) => {
                        setStartDate(s);
                        setEndDate(e);
                    }}
                />
            </div>

            {/* Loading */}
            {isLoading && <LoadingSkeleton />}

            {/* Error */}
            {isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-red-900">
                            Não foi possível carregar o relatório
                        </p>
                        <p className="text-xs text-red-700 mt-0.5">
                            Tente novamente em instantes ou ajuste os filtros.
                        </p>
                    </div>
                </div>
            )}

            {/* Conteudo */}
            {!isLoading && !isError && summary && (
                <>
                    {/* Cards principais */}
                    <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
                        <StatCard
                            label="Pageviews"
                            value={fmt.number(summary.total_pageviews)}
                            icon={Eye}
                            details={[
                                {
                                    key: 'Visitantes únicos',
                                    val: fmt.number(summary.total_unique_visitors),
                                },
                            ]}
                        />
                        <StatCard
                            label="Sessões"
                            value={fmt.number(summary.total_unique_sessions)}
                            icon={Users}
                        />
                        <StatCard
                            label="RPS"
                            value={fmt.currency(summary.avg_rps)}
                            icon={TrendingUp}
                            hint="Revenue por sessão"
                            details={[
                                {
                                    key: 'Revenue total',
                                    val: fmt.currency(summary.total_revenue),
                                },
                            ]}
                        />
                        <StatCard
                            label="RPM"
                            value={fmt.currency(summary.avg_rpm)}
                            icon={DollarSign}
                            hint="Revenue por mil PV"
                        />
                        <StatCard
                            label="IPVU"
                            value={summary.avg_ipvu.toFixed(2)}
                            icon={Activity}
                            hint="Impressões por visita única"
                            details={[
                                {
                                    key: 'Impressões',
                                    val: fmt.number(summary.total_impressions),
                                },
                            ]}
                        />
                    </div>

                    {/* Tabs */}
                    <Tabs
                        value={activeTab}
                        onValueChange={(v) => setActiveTab(v as TabValue)}
                        className="space-y-3"
                    >
                        <TabsList className="h-10 bg-gray-100">
                            <TabsTrigger value="domains" className="gap-1.5">
                                <Globe className="h-3.5 w-3.5" />
                                Domínios
                                <span className="ml-1 text-[10px] text-gray-500">
                                    {byDomain.length}
                                </span>
                            </TabsTrigger>
                            <TabsTrigger value="urls" className="gap-1.5">
                                <TrendingUp className="h-3.5 w-3.5" />
                                URLs
                                <span className="ml-1 text-[10px] text-gray-500">
                                    {topUrls.length}
                                </span>
                            </TabsTrigger>
                            <TabsTrigger value="countries" className="gap-1.5">
                                <MapPin className="h-3.5 w-3.5" />
                                Países
                                <span className="ml-1 text-[10px] text-gray-500">
                                    {byCountry.length}
                                </span>
                            </TabsTrigger>
                            <TabsTrigger value="paths" className="gap-1.5">
                                <Languages className="h-3.5 w-3.5" />
                                Paths
                                <span className="ml-1 text-[10px] text-gray-500">
                                    {byPath.length}
                                </span>
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="domains" className="mt-0">
                            <DomainsTab data={byDomain} />
                        </TabsContent>
                        <TabsContent value="urls" className="mt-0">
                            <UrlsTab data={topUrls} />
                        </TabsContent>
                        <TabsContent value="countries" className="mt-0">
                            <CountriesTab data={byCountry} />
                        </TabsContent>
                        <TabsContent value="paths" className="mt-0">
                            <PathsTab data={byPath} />
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-5">
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div
                        key={i}
                        className="rounded-lg border border-gray-200 bg-white p-4 h-[120px] animate-pulse"
                    >
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
                        <div className="h-7 bg-gray-200 rounded w-2/3" />
                    </div>
                ))}
            </div>
            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            <div className="rounded-lg border border-gray-200 bg-white h-80 animate-pulse" />
        </div>
    );
}
